import { useMemo, useState } from 'react'

import { resourceTag, useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  NEW_PRODUCT_DRAFT,
  OWN_CATALOG_CAPABILITIES,
  OWN_PRODUCT_TAG,
  OWN_WINDOWS,
  OwnProductCardScreen,
  draftOf,
  draftProblem,
  inputOf,
  ownArchiveProduct,
  ownChangeProduct,
  ownCreateProduct,
  ownGroups,
  ownNomenclatureApi,
  ownProduct,
  ownRestoreProduct,
  productCardVm,
  type OwnProductCardActions,
  type ProductCardMode,
  type ProductDraft,
} from '@krp/resource-own-ui'

import { useAppDispatch } from '../../app/hooks'
import { useOwnCapability } from './useOwnCapability'

type Command = 'save' | 'archive' | 'restore'

/**
 * Binding карточки товара своей базы и окна «Новый товар» (RP-OWN3). rid и товар — из адреса окна. Черновик правки живёт,
 * пока открыт экран; запись — с версией, которую видел человек: отказ службы (в том числе «изменено другим») показывается
 * дословно, «Перечитать» берёт свежую карточку и сбрасывает черновик. Созданный товар открывается своей карточкой.
 */
export function OwnProductCardBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const productId = window.params.id ?? ''
  const creating = window.kind === OWN_WINDOWS.productCreate
  const canWrite = useOwnCapability(resourceId, OWN_CATALOG_CAPABILITIES.catalogWrite)
  const canSeePrices = useOwnCapability(resourceId, OWN_CATALOG_CAPABILITIES.pricesRead)

  const productArgs = useMemo(
    () => (creating || resourceId === '' || productId === '' ? null : { resourceId, productId, on: null }),
    [creating, productId, resourceId],
  )
  const product = useQueryResult(ownProduct, productArgs)
  const groupArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const groups = useQueryResult(ownGroups, groupArgs)

  const [editing, setEditing] = useState<ProductDraft | null>(creating ? NEW_PRODUCT_DRAFT : null)
  const [done, setDone] = useState<string | null>(null)
  const [last, setLast] = useState<Command>('save')
  const [create, createState] = ownCreateProduct.useMutation()
  const [change, changeState] = ownChangeProduct.useMutation()
  const [archive, archiveState] = ownArchiveProduct.useMutation()
  const [restore, restoreState] = ownRestoreProduct.useMutation()
  const command = creating ? createState : last === 'archive' ? archiveState : last === 'restore' ? restoreState : changeState

  const mode: ProductCardMode = creating ? 'create' : editing === null ? 'view' : 'edit'
  const draft = editing ?? (product.data === undefined ? NEW_PRODUCT_DRAFT : draftOf(product.data))
  const vm = productCardVm({
    mode,
    product: creating ? null : product,
    groups: groups.data ?? [],
    draft,
    command,
    done,
    canWrite,
    canSeePrices,
  })

  const actions = useMemo<OwnProductCardActions>(() => {
    const begin = (next: Command) => {
      setLast(next)
      setDone(null)
      createState.reset()
      changeState.reset()
      archiveState.reset()
      restoreState.reset()
    }
    return {
      edit: () => {
        if (product.data === undefined) return
        begin('save')
        setEditing(draftOf(product.data))
      },
      cancel: () => {
        begin('save')
        setEditing(null)
      },
      draftChanged: setEditing,
      save: () => {
        if (editing === null || draftProblem(editing, creating ? 'create' : 'edit') !== null) return
        begin('save')
        if (creating) {
          void create({ resourceId, product: inputOf(editing) })
            .unwrap()
            .then((created) => window.replaceWith(OWN_WINDOWS.product, { rid: resourceId, id: created.id }))
            .catch(() => undefined)
          return
        }
        if (product.data === undefined) return
        void change({ resourceId, productId, expectedVersion: product.data.version, product: inputOf(editing) })
          .unwrap()
          .then((saved) => {
            setEditing(null)
            setDone(`Сохранено: «${saved.name}», версия ${saved.version}`)
          })
          .catch(() => undefined)
      },
      archive: () => {
        if (product.data === undefined) return
        begin('archive')
        void archive({ resourceId, productId, expectedVersion: product.data.version })
          .unwrap()
          .then((saved) => setDone(`«${saved.name}» убран в архив`))
          .catch(() => undefined)
      },
      restore: () => {
        if (product.data === undefined) return
        begin('restore')
        void restore({ resourceId, productId, expectedVersion: product.data.version })
          .unwrap()
          .then((saved) => setDone(`«${saved.name}» возвращён из архива`))
          .catch(() => undefined)
      },
      reload: () => {
        begin('save')
        setEditing(null)
        dispatch(ownNomenclatureApi.util.invalidateTags([resourceTag(OWN_PRODUCT_TAG, resourceId, productId)]))
      },
      openPrices: () => openWindow(OWN_WINDOWS.productPrices, { rid: resourceId, id: productId }),
      close: window.close,
    }
  }, [
    archive,
    archiveState,
    change,
    changeState,
    create,
    createState,
    creating,
    dispatch,
    editing,
    openWindow,
    product.data,
    productId,
    resourceId,
    restore,
    restoreState,
    window,
  ])

  return <OwnProductCardScreen vm={vm} actions={actions} />
}
